import Dexie, { type Table } from 'dexie';
import type { Account, ComposerDraft, Contact, Conversation, FriendRequest, Id, Me, Message, OutgoingDraft, Pin, Reaction, ReplyTo, SyncSnapshot, Update } from '@/model';
import { decodeAccount, decodeConversation, decodeMe, decodeMessage, decodeSnapshot, integer } from './decode';
import { initialProtocol, knownUpdateTypes } from './protocol';
import { normalizeTranscripts, projectTranscript, rememberTranscript } from './transcripts';
import type { DocumentDraftRow, DocumentCandidate, DocumentChoice, DocumentMove } from './document-journal';

export interface SyncMeta { id: string; pts: number; stream_epoch: string; generation: number; protocol: number; snapshot_generation?: number; pins_revision?: number; pins_update_revision?: number }
export interface StoredMessage extends Message { hidden?: boolean }
export interface OutboxRow {
  client_id: Id; conversation_id: Id; draft: OutgoingDraft; created_at: number;
  state: 'sending' | 'failed'; attempts: number; next_attempt_at: number;
  uploaded: (Id | null)[]; progress?: number; failure?: string; rate_limited_until?: number; forward_of?: Id;
}
export interface DraftRow extends ComposerDraft { conversation_id: Id; remote_revision?: number; local_revision?: number; local?: boolean; source?: 'local' | 'remote'; remote_draft?: Record<string, unknown> }
export interface ReadMarkRow { conversation_id: Id; seq: number; attempts: number; next_attempt_at: number }
export interface MediaCacheRow { key: string; media_id: Id; variant: string; accessed_at: number; bytes: number }
interface ReactionRow { message_id: Id; revision: number; reactions: Reaction[] }
interface TombstoneRow { id: Id; conversation_id: Id; deleted_at: string | null; hidden: boolean }

export class AccountStore extends Dexie {
  readonly accountId: Id;
  meta!: Table<SyncMeta, string>;
  accounts!: Table<Account, Id>;
  conversations!: Table<Conversation, Id>;
  messages!: Table<StoredMessage, Id>;
  contacts!: Table<Contact, Id>;
  requests!: Table<FriendRequest, Id>;
  blocks!: Table<{ account_id: Id }, Id>;
  pins!: Table<Pin, Id>;
  me!: Table<{ id: string; value: Me }, string>;
  outbox!: Table<OutboxRow, Id>;
  drafts!: Table<DraftRow, Id>;
  mediaCache!: Table<MediaCacheRow, string>;
  readMarks!: Table<ReadMarkRow, Id>;
  erasedConversations!: Table<{ id: Id }, Id>;
  mediaUploads!: Table<{ key: string; upload_id: Id }, string>;
  reactions!: Table<ReactionRow, Id>;
  tombstones!: Table<TombstoneRow, Id>;
  transcripts!: Table<{ media_id: Id; revision: number; value: unknown }, Id>;
  conversationIntents!: Table<{ id: Id; notify: 'all' | 'mentions'; mute_until: string | null }, Id>;
  contactNotes!: Table<{ account_id: Id; alias?: string | null; revision?: number; [key: string]: unknown }, Id>;
  coverage!: Table<{ id: string; conversation_id: Id; from: number; to: number; generation: number }, string>;
  seen!: Table<{ conversation_id: Id; seq: number; generation: number }, [Id, number]>;
  fetches!: Table<{ conversation_id: Id }, Id>;
  visibility!: Table<{ conversation_id: Id; cleared_seq: number; hidden_seq: number | null; blocked?: boolean }, Id>;
  projectAccess!: Table<{ id: Id; kind: string }, Id>;
  voiceReviews!: Table<{ message_id: Id; media_id: Id; revision: number; value: Record<string, unknown> }, [Id, Id]>;
  resourceFetches!: Table<{ id: string }, string>;
  documentDrafts!: Table<DocumentDraftRow, string>;
  documentCandidates!: Table<DocumentCandidate, string>;
  documentChoices!: Table<DocumentChoice, string>;
  documentMoves!: Table<DocumentMove, string>;
  documentClock!: Table<{ id: string; value: number }, string>;
  constructor(accountId: Id) {
    super(`hylo-web-${accountId}`); this.accountId = accountId;
    // Append versions only. A shipped version must never be changed or replaced by an erase.
    this.version(1).stores({ meta: 'id', accounts: 'id,handle', conversations: 'id,updated_at', messages: 'id,[conversation_id+seq],client_id,[sender_id+client_id]', contacts: 'account_id', requests: 'id,from_id,to_id,status', blocks: 'account_id', pins: 'conversation_id,position', me: 'id', outbox: 'client_id,conversation_id,created_at,next_attempt_at,state', drafts: 'conversation_id', mediaCache: 'key,accessed_at', reactions: 'message_id', tombstones: 'id,conversation_id', transcripts: 'media_id', fetches: 'conversation_id' });
    this.version(2).stores({ readMarks: 'conversation_id,next_attempt_at' });
    this.version(3).stores({ erasedConversations: 'id' });
    this.version(4).stores({ mediaUploads: 'key,upload_id' });
    this.version(5).stores({ coverage: 'id,conversation_id', seen: '[conversation_id+seq],conversation_id' });
    this.version(6).stores({ conversationIntents: 'id', contactNotes: 'account_id' });
    this.version(7).stores({ visibility: 'conversation_id' });
    this.version(8).stores({ projectAccess: 'id' });
    this.version(9).stores({ voiceReviews: '[message_id+media_id],media_id' });
    this.version(10).stores({ resourceFetches: 'id' });
    this.version(11).stores({ documentDrafts: 'key,scope,owner', documentCandidates: 'key,realm', documentChoices: 'key', documentMoves: 'id', documentClock: 'id' });
  }
}

const emptyMeta = (): SyncMeta => ({ id: 'sync', pts: 0, stream_epoch: '', generation: 0, protocol: initialProtocol });

export async function advancePinsRevision(db: AccountStore, fromUpdate = false): Promise<number> {
  const meta = await db.meta.get('sync');
  const revision = (meta?.pins_revision ?? 0) + 1;
  if (meta) await db.meta.put({ ...meta, pins_revision: revision, pins_update_revision: (meta.pins_update_revision ?? 0) + Number(fromUpdate) });
  return meta ? revision : 0;
}

/** Caller owns the transaction. This clock changes only for authoritative pin state, including equal echoes. */
export async function replacePins(db: AccountStore, pins: Pin[], fromUpdate = false): Promise<void> {
  await db.pins.clear(); await db.pins.bulkPut(pins); await advancePinsRevision(db, fromUpdate);
}

async function removePin(db: AccountStore, id: Id): Promise<void> {
  if (!await db.pins.get(id)) return;
  await db.pins.delete(id); await advancePinsRevision(db, true);
}

export async function fenceStore(db: AccountStore): Promise<SyncMeta> {
  return db.transaction('rw', db.meta, async () => { const meta = { ...(await db.meta.get('sync') ?? emptyMeta()) }; meta.snapshot_generation ??= meta.stream_epoch ? meta.generation : undefined; meta.generation++; await db.meta.put(meta); return meta; });
}

function redact(message: StoredMessage, deletedAt: string | null = message.deleted_at): StoredMessage {
  return { ...message, parts: [], reactions: [], reply_to: null, mentions: [], deleted_at: deletedAt };
}

export function readableWindow(c: Conversation): { floor: number; upper: number } {
  return { floor: Math.max(Number(c.me.history_from_seq ?? (c.me.left_seq !== null && c.me.access !== 'project' ? c.me.joined_seq : 0)), Number(c.me.cleared_seq ?? 0)), upper: c.me.access === 'project' ? Infinity : c.me.left_seq ?? Infinity };
}

/** Hydrated quotes have their own visibility: a late page cannot restore an unavailable target's text. */
export async function readableReply(db: AccountStore, conversationId: Id, reply: ReplyTo | null): Promise<ReplyTo | null> {
  if (!reply) return null;
  const [target, marker, c, visibility] = await Promise.all([db.messages.get(reply.id), db.tombstones.get(reply.id), db.conversations.get(conversationId), db.visibility.get(conversationId)]);
  const window = c ? readableWindow(c) : { floor: visibility?.cleared_seq ?? 0, upper: Infinity };
  const unavailable = reply.kind === 'deleted' || target?.deleted_at || target?.hidden || marker?.deleted_at || marker?.hidden || reply.seq > 0 && (reply.seq <= window.floor || reply.seq > window.upper);
  return unavailable && (reply.kind !== 'deleted' || reply.preview !== '') ? { ...reply, kind: 'deleted', preview: '' } : reply;
}

async function reconcileReplies(db: AccountStore, conversationId: Id, targetId?: Id): Promise<void> {
  const rows = await db.messages.where('[conversation_id+seq]').between([conversationId, Dexie.minKey], [conversationId, Dexie.maxKey]).filter(m => Boolean(m.reply_to) && (!targetId || m.reply_to?.id === targetId)).toArray();
  for (const m of rows) {
    const reply = await readableReply(db, conversationId, m.reply_to);
    if (reply !== m.reply_to) await db.messages.put({ ...m, reply_to: reply });
  }
  const c = await db.conversations.get(conversationId);
  if (c?.last_message?.reply_to && (!targetId || c.last_message.reply_to.id === targetId)) {
    const reply = await readableReply(db, conversationId, c.last_message.reply_to);
    if (reply !== c.last_message.reply_to) await db.conversations.put({ ...c, last_message: { ...c.last_message, reply_to: reply } });
  }
}

async function retainVisibility(db: AccountStore, c: Conversation): Promise<Conversation> {
  const held = await db.conversations.get(c.id); const remembered = await db.visibility.get(c.id);
  const cleared = Math.max(Number(c.me.cleared_seq ?? 0), Number(held?.me.cleared_seq ?? 0), remembered?.cleared_seq ?? 0);
  const hides = [c.me.hidden_seq, held?.me.hidden_seq, remembered?.hidden_seq].filter((n): n is number => typeof n === 'number');
  const hidden = hides.length ? Math.max(...hides) : null;
  if (remembered || cleared > 0 || hidden !== null) await db.visibility.put({ ...remembered, conversation_id: c.id, cleared_seq: cleared, hidden_seq: hidden });
  const priorMembership = c.me.access === 'project' && held ? { joined_seq: c.me.joined_seq || held.me.joined_seq, left_seq: c.me.left_seq ?? held.me.left_seq } : {};
  return { ...c, me: { ...c.me, ...priorMembership, cleared_seq: cleared, hidden_seq: hidden } };
}

/** Caller owns the transaction. Personal clear/hide positions survive all projection replacements. */
export async function putConversation(db: AccountStore, value: Conversation): Promise<void> {
  const held = await db.conversations.get(value.id);
  const c = await retainVisibility(db, value); const floor = Number(c.me.cleared_seq);
  c._notification_revision = Number(held?._notification_revision ?? 0) + Number(Boolean(held && (held.me.notify !== c.me.notify || (held.me.mute_until ?? null) !== (c.me.mute_until ?? null))));
  const window = readableWindow(c);
  const rows = await db.messages.where('[conversation_id+seq]').between([c.id, Dexie.minKey], [c.id, Dexie.maxKey]).toArray();
  for (const m of rows) {
    if (m.seq <= floor) await db.messages.put({ ...redact(m), hidden: true });
    else if (m.seq <= window.floor || m.seq > window.upper) await db.messages.delete(m.id);
  }
  if (c.last_message && (c.last_message.seq <= window.floor || c.last_message.seq > window.upper)) {
    c.last_message = await db.messages.where('[conversation_id+seq]').between([c.id, window.floor], [c.id, window.upper === Infinity ? Dexie.maxKey : window.upper], false, true).filter(m => !m.hidden).reverse().first() ?? null;
    c.last_message_at = c.last_message?.created_at ?? null;
  }
  c.last_seq = Math.min(c.last_seq, window.upper);
  await db.conversations.put(c);
  for (const range of await db.coverage.where('conversation_id').equals(c.id).toArray()) {
    const from = Math.max(range.from, window.floor + 1); const to = Math.min(range.to, window.upper);
    if (to < from) await db.coverage.delete(range.id);
    else if (from !== range.from || to !== range.to) await db.coverage.put({ ...range, from, to });
  }
  await db.seen.where('conversation_id').equals(c.id).filter(row => row.seq <= window.floor || row.seq > window.upper).delete();
  await reconcileReplies(db, c.id);
}

/** Access loss differs from permanent erasure: personal unsent work remains durable, but cannot send. */
export async function revokeConversationAccess(db: AccountStore, id: Id, repair = false): Promise<void> {
  const c = await db.conversations.get(id);
  if (c?.me.access === 'project' && c.me.left_seq !== null) {
    await putConversation(db, { ...c, members: [], me: { ...c.me, access: 'former', history_from_seq: Math.max(Number(c.me.history_from_seq ?? 0), c.me.joined_seq), unread_count: 0, first_unread_seq: null } });
  } else {
    if (c) await retainVisibility(db, c);
    const visibility = await db.visibility.get(id);
    await db.visibility.put({ conversation_id: id, cleared_seq: 0, hidden_seq: null, ...visibility, blocked: true });
    await db.conversations.delete(id); await removePin(db, id);
    await db.messages.where('[conversation_id+seq]').between([id, Dexie.minKey], [id, Dexie.maxKey]).delete();
    await db.coverage.where('conversation_id').equals(id).delete(); await db.seen.where('conversation_id').equals(id).delete();
  }
  await db.outbox.where('conversation_id').equals(id).modify({ state: 'failed', failure: 'not_member' });
  if (repair) await db.fetches.put({ conversation_id: id }); else await db.fetches.delete(id);
}

async function mergeInTransaction(db: AccountStore, incoming: Message): Promise<StoredMessage> {
  const m = decodeMessage(incoming);
  if (await db.erasedConversations.get(m.conversation_id)) return redact(m);
  const marker = await db.tombstones.get(m.id);
  if (m.deleted_at || m.hidden) await db.tombstones.put({ id: m.id, conversation_id: m.conversation_id, deleted_at: marker?.deleted_at ?? m.deleted_at, hidden: Boolean(marker?.hidden || m.hidden) });
  const visibility = await db.visibility.get(m.conversation_id);
  if (visibility?.blocked) return { ...redact(m), hidden: true };
  const held = await db.messages.get(m.id);
  const c = await db.conversations.get(m.conversation_id);
  const floor = Math.max(Number(c?.me.cleared_seq ?? 0), visibility?.cleared_seq ?? 0);
  if (c && m.seq > floor && m.seq > 0 && (m.seq <= readableWindow(c).floor || m.seq > readableWindow(c).upper)) return { ...redact(m), hidden: true };
  const cleared = floor >= m.seq && m.seq > 0;
  const hidden = m.hidden || held?.hidden || marker?.hidden || cleared;
  const audioIds = new Set<Id>();
  if (!hidden && !m.deleted_at && !held?.deleted_at && !marker?.deleted_at) for (const part of m.parts) {
    if (part.type === 'media' && part.kind === 'audio' && typeof part.media_id === 'string') { await rememberTranscript(db, part.media_id, m.id, part.transcript); audioIds.add(part.media_id); }
  }
  const oldReactions = await db.reactions.get(m.id);
  if (!oldReactions || m.revision >= oldReactions.revision) await db.reactions.put({ message_id: m.id, revision: m.revision, reactions: m.reactions });
  let next: StoredMessage = held && held.revision > m.revision ? held : { ...m };
  if (oldReactions && oldReactions.revision > m.revision) next = { ...next, reactions: oldReactions.reactions };
  if (held?.deleted_at || marker?.deleted_at || m.deleted_at) {
    next = redact(next, held?.deleted_at ?? marker?.deleted_at ?? m.deleted_at);
    await db.tombstones.put({ id: m.id, conversation_id: m.conversation_id, deleted_at: next.deleted_at, hidden: Boolean(hidden) });
  } else if (hidden) next = { ...redact(next), hidden: true };
  if (hidden) next.hidden = true;
  if (m.hidden) await db.tombstones.put({ id: m.id, conversation_id: m.conversation_id, deleted_at: next.deleted_at, hidden: true });
  next = { ...next, reply_to: await readableReply(db, m.conversation_id, next.reply_to) };
  if (next.parts.some(part => part.type === 'media' && part.kind === 'audio')) next = await normalizeTranscripts(db, next);
  if (held?._counted_unread) next._counted_unread = true;
  await db.messages.put(next);
  const pending = await db.outbox.get(m.client_id);
  if (pending && m.sender_id === (await db.me.get('me'))?.value.account.id) await db.outbox.delete(m.client_id);
  if (c?.last_message?.id === m.id) await db.conversations.put({ ...c, last_message: next });
  if (next.hidden || next.deleted_at) await reconcileReplies(db, m.conversation_id, m.id);
  for (const id of audioIds) await projectTranscript(db, id);
  return next;
}
/** The shared merge path for history, send/edit responses, snapshots and durable updates. */
export function mergeMessage(db: AccountStore, message: Message): Promise<StoredMessage> {
  return db.transaction('rw', [db.messages, db.reactions, db.tombstones, db.conversations, db.outbox, db.me, db.erasedConversations, db.visibility, db.transcripts, db.voiceReviews], () => mergeInTransaction(db, message));
}

/** The caller owns the transaction. Local seen evidence may lead a queued/rotating server read. */
export async function advanceLocalRead(db: AccountStore, id: Id, seq: number): Promise<void> {
  const c = await db.conversations.get(id); if (!c) return;
  const target = Math.min(seq, c.me.left_seq ?? c.last_seq); if (target <= c.me.read_seq) return;
  const myId = (await db.me.get('me'))?.value.account.id ?? db.accountId;
  const seen = await db.messages.where('[conversation_id+seq]').between([id, Math.max(c.me.read_seq, c.me.joined_seq)], [id, target], false, true).filter(m => m.sender_id !== myId && m.kind === 'user' && !m.deleted_at && !m.hidden).count();
  const unread = target >= Number(c._unread_through_seq ?? c.last_seq) ? 0 : Math.max(0, c.me.unread_count - seen);
  await db.conversations.put({ ...c, me: { ...c.me, read_seq: target, unread_count: unread, first_unread_seq: unread > 0 && c.me.first_unread_seq !== null && c.me.first_unread_seq > target ? c.me.first_unread_seq : null } });
}

/** Replace only server projections. Pending sends (including Blobs) and composer drafts survive. */
export async function applySnapshot(db: AccountStore, value: SyncSnapshot, options: { generation?: number; protocol?: number } = {}): Promise<boolean> {
  const s = decodeSnapshot(value);
  return db.transaction('rw', db.tables, async () => {
    const old = await db.meta.get('sync') ?? emptyMeta();
    if (options.generation !== undefined && old.generation !== options.generation) return false;
    for (const c of await db.conversations.toArray()) await retainVisibility(db, c);
    const conversations: Conversation[] = [];
    for (const c of s.conversations) { await db.visibility.update(c.id, { blocked: false }); conversations.push(await retainVisibility(db, c)); }
    for (const table of [db.contactNotes, db.coverage, db.seen, db.accounts, db.conversations, db.messages, db.contacts, db.requests, db.blocks, db.pins, db.me, db.reactions, db.fetches, db.resourceFetches]) await table.clear();
    await db.accounts.bulkPut([...s.accounts, s.me.account]); await db.me.put({ id: 'me', value: s.me });
    await db.projectAccess.clear();
    if (Array.isArray(s.projects)) for (const project of s.projects as { id: Id; kind: string }[]) await db.projectAccess.put({ id: project.id, kind: project.kind });
    const erased = new Set(await db.erasedConversations.toCollection().primaryKeys());
    for (const c of conversations.filter(c => !erased.has(c.id))) await putConversation(db, { ...c, _unread_through_seq: c.last_seq, _server_counted_through_seq: c.last_seq });
    if (Array.isArray(s.contact_notes)) await db.contactNotes.bulkPut(s.contact_notes as { account_id: Id }[]);
    await db.contacts.bulkPut(s.contacts); await db.requests.bulkPut([...s.requests.incoming, ...s.requests.outgoing]);
    await db.blocks.bulkPut(s.blocks.map(account_id => ({ account_id }))); await db.pins.bulkPut(s.pins);
    const generation = options.generation ?? old.generation + 1;
    for (const c of conversations) if (c.last_message) { await mergeInTransaction(db, c.last_message); await recordCoverage(db, c.id, c.last_message.seq, c.last_message.seq, generation); }
    for (const mark of await db.readMarks.toArray()) await advanceLocalRead(db, mark.conversation_id, mark.seq);
    if (Array.isArray(s.drafts)) for (const value of s.drafts) await applyRemoteDraft(db, value as Record<string, unknown>);
    await db.meta.put({ id: 'sync', pts: s.pts, stream_epoch: s.stream_epoch, generation, snapshot_generation: generation, protocol: options.protocol ?? old.protocol });
    return true;
  });
}

export async function importConversation(db: AccountStore, value: Conversation, accounts: Account[] = [], positional = false, accessRepair = false): Promise<void> {
  await db.transaction('rw', db.tables, async () => {
    const c = decodeConversation(value); if (await db.erasedConversations.get(c.id)) return; c._unread_through_seq = c.last_seq; c._server_counted_through_seq = c.last_seq; const held = await db.conversations.get(c.id);
    if (accessRepair) await db.visibility.update(c.id, { blocked: false });
    else if ((await db.visibility.get(c.id))?.blocked) return;
    if (held && !positional) {
      c._unread_through_seq = held._unread_through_seq ?? held.last_seq; c._server_counted_through_seq = held._server_counted_through_seq ?? held.last_seq;
      c.me = { ...held.me, ...c.me, read_seq: Math.max(c.me.read_seq, held.me.read_seq), unread_count: held.me.unread_count, first_unread_seq: held.me.first_unread_seq };
      if (held.last_seq > c.last_seq) { c.last_seq = held.last_seq; c.last_message = held.last_message; c.last_message_at = held.last_message_at; }
      else if (value.me.read_seq === held.me.read_seq) c.me.first_unread_seq = value.me.first_unread_seq;
    }
    await db.accounts.bulkPut(accounts.map(decodeAccount)); await putConversation(db, c);
    if (c.last_message) await mergeInTransaction(db, c.last_message);
    await db.fetches.delete(c.id);
  });
}

export async function applyRemoteDraft(db: AccountStore, data: Record<string, unknown>): Promise<void> {
  const conversationId = String(data.conversation_id ?? ''); if (!conversationId) return;
  const old = await db.drafts.get(conversationId);
  if ((old?.remote_revision ?? 0) > integer(data.revision)) return;
  if (old?.local) { await db.drafts.update(conversationId, { remote_revision: integer(data.revision), remote_draft: { ...data } }); return; }
  const row: DraftRow = { ...old, replyToId: undefined, source: 'remote', local_revision: (old?.local_revision ?? 0) + 1, conversation_id: conversationId, text: typeof data.text === 'string' ? data.text : '', updatedAt: typeof data.updated_at === 'string' ? Date.parse(data.updated_at) : 0, remote_revision: integer(data.revision), local: false };
  if (typeof data.reply_to_id === 'string') row.replyToId = data.reply_to_id;
  if (old?.editingMessageId) { row.text = old.text; row.replyToId = old.replyToId; row.suspended = { ...old.suspended, text: typeof data.text === 'string' ? data.text : '', replyToId: typeof data.reply_to_id === 'string' ? data.reply_to_id : undefined }; }
  row.remote_draft = undefined;
  await db.drafts.put(row);
}

async function applyCore(db: AccountStore, u: Update, myId: Id): Promise<void> {
  const d = u.data;
  const cid = typeof d.conversation_id === 'string' ? d.conversation_id : undefined;
  let c = cid ? await db.conversations.get(cid) : undefined;
  if (cid && !c) await db.fetches.put({ conversation_id: cid });
  switch (u.type) {
    case 'message.new': case 'message.edited': case 'message.deleted': {
      const m = decodeMessage(d.message); c = await db.conversations.get(m.conversation_id);
      const previous = await db.messages.get(m.id);
      const merged = await mergeInTransaction(db, m);
      if (!c) { await db.fetches.put({ conversation_id: m.conversation_id }); break; }
      c = await db.conversations.get(c.id) ?? c;
      const through = Number(c._unread_through_seq ?? c.last_seq);
      if (u.type === 'message.new') {
        const window = readableWindow(c);
        if (m.seq <= window.floor || m.seq > window.upper) break;
        const generation = (await db.meta.get('sync'))?.generation;
        if (generation !== undefined) await recordCoverage(db, c.id, Math.min(c.last_seq + 1, m.seq), m.seq, generation);
        const unread = !merged.hidden && m.seq > through && m.sender_id !== myId && m.kind === 'user' && !m.deleted_at && m.seq > Math.max(c.me.read_seq, c.me.joined_seq) && (c.me.left_seq === null || m.seq <= c.me.left_seq);
        const stored = await db.messages.get(m.id);
        if (stored && unread) await db.messages.put({ ...stored, _counted_unread: true });
        c = { ...c, _unread_through_seq: Math.max(through, m.seq), last_seq: Math.max(c.last_seq, m.seq), ...(m.seq >= c.last_seq && !merged.hidden ? { last_message_at: m.created_at, last_message: stored ?? merged } : {}), me: { ...c.me, unread_count: c.me.unread_count + Number(unread), first_unread_seq: unread ? c.me.first_unread_seq ?? m.seq : c.me.first_unread_seq } };
        await db.conversations.put(c);
      } else if (u.type === 'message.deleted') {
        const fromSnapshot = m.seq <= Number(c._server_counted_through_seq ?? 0);
        const counted = !previous?.deleted_at && (fromSnapshot ? m.sender_id !== myId && m.kind === 'user' : previous?._counted_unread === true);
        if (counted && m.seq > Math.max(c.me.read_seq, c.me.joined_seq) && m.seq <= through) {
          const count = Math.max(0, c.me.unread_count - 1);
          await db.conversations.put({ ...c, me: { ...c.me, unread_count: count, first_unread_seq: count === 0 || c.me.first_unread_seq === m.seq ? null : c.me.first_unread_seq } });
        }
      }
      break;
    }
    case 'reaction.changed': {
      const mid = String(d.message_id); const revision = integer(d.revision);
      const old = await db.reactions.get(mid); const m = await db.messages.get(mid);
      if (revision < Math.max(old?.revision ?? 0, m?.revision ?? 0) || m?.deleted_at) break;
      const reactions = Array.isArray(d.reactions) ? d.reactions as Reaction[] : [];
      await db.reactions.put({ message_id: mid, revision, reactions });
      if (m) { const next = { ...m, reactions: m.hidden ? [] : reactions }; await db.messages.put(next); if (c?.last_message?.id === mid) await db.conversations.put({ ...c, last_message: next }); }
      break;
    }
    case 'read.changed': {
      const pending = cid ? await db.readMarks.get(cid) : undefined;
      if (pending && integer(d.read_seq) >= pending.seq) await db.readMarks.delete(pending.conversation_id);
      if (c && integer(d.read_seq) >= c.me.read_seq) await db.conversations.put({ ...c, _server_counted_through_seq: c._unread_through_seq ?? c.last_seq, me: { ...c.me, read_seq: integer(d.read_seq), unread_count: integer(d.unread_count), first_unread_seq: integer(d.unread_count) > 0 && typeof d.first_unread_seq === 'number' ? d.first_unread_seq : null } });
      break;
    }
    case 'receipt.changed': case 'delivery.changed':
      if (c && d.account_id !== myId) {
        const delivery = u.type === 'delivery.changed'; const seq = delivery ? 'delivered_seq' : 'read_seq'; const at = delivery ? 'delivered_at' : 'read_at';
        await db.conversations.put({ ...c, members: c.members.map(m => m.account_id !== d.account_id || delivery && integer(d[seq]) < Number(m[seq] ?? 0) ? m : { ...m, [seq]: integer(d[seq]), [at]: d[at] ?? null }) });
      }
      break;
    case 'conversation.created': {
      const conversation = decodeConversation(d.conversation);
      if (await db.erasedConversations.get(conversation.id)) break;
      await db.visibility.update(conversation.id, { blocked: false });
      if (Array.isArray(d.accounts)) await db.accounts.bulkPut(d.accounts.map(decodeAccount));
      const held = await db.conversations.get(conversation.id);
      conversation._unread_through_seq = held?._unread_through_seq ?? Math.max(conversation.me.read_seq, conversation.me.joined_seq);
      conversation._server_counted_through_seq = held?._server_counted_through_seq ?? 0;
      if (held) conversation.me = { ...conversation.me, read_seq: Math.max(held.me.read_seq, conversation.me.read_seq), unread_count: held.me.unread_count, first_unread_seq: held.me.first_unread_seq };
      await putConversation(db, conversation); if (conversation.last_message) await mergeInTransaction(db, conversation.last_message);
      await db.fetches.delete(conversation.id); break;
    }
    case 'conversation.updated':
      if (d.conversation) { await importConversation(db, decodeConversation(d.conversation), Array.isArray(d.accounts) ? d.accounts.map(decodeAccount) : []); }
      else if (c) { const patch = { ...d }; delete patch.conversation_id; await putConversation(db, { ...c, ...patch, id: c.id, me: c.me }); }
      break;
    case 'conversation.moved':
      if (c) {
        const destination = { ...c, project_id: typeof d.project_id === 'string' ? d.project_id : null, project_kind: d.project_kind };
        const known = typeof d.project_id === 'string' && (await db.projectAccess.get(d.project_id))?.kind === 'named';
        await putConversation(db, destination);
        if (c.me.access === 'project' && !known) await revokeConversationAccess(db, c.id, true);
      }
      if (cid) await db.fetches.put({ conversation_id: cid });
      break;
    case 'member.joined':
      if (d.account) await db.accounts.put(decodeAccount(d.account));
      if (c && d.member) {
        const member = d.member as Conversation['members'][number];
        const isMe = member.account_id === myId;
        await db.conversations.put({ ...c, members: [...c.members.filter(m => m.account_id !== member.account_id), member], me: isMe ? { ...c.me, role: member.role, joined_seq: member.joined_seq, left_seq: null } : c.me });
        if (isMe) await db.fetches.put({ conversation_id: c.id });
      }
      break;
    case 'member.left':
      if (c) {
        const own = d.account_id === myId; const project = d.access === 'project';
        const me = own ? { ...c.me, ...(project ? { role: null } : {}), access: project ? 'project' : 'former', history_from_seq: typeof d.history_from_seq === 'number' ? d.history_from_seq : c.me.joined_seq, left_seq: integer(d.left_seq), unread_count: 0, first_unread_seq: null } : c.me;
        await putConversation(db, { ...c, members: own && !project ? [] : c.members.filter(m => m.account_id !== d.account_id), me: me as Conversation['me'] });
        if (own) { await db.outbox.where('conversation_id').equals(c.id).modify({ state: 'failed', failure: 'not_member' }); await db.fetches.put({ conversation_id: c.id }); }
      }
      break;
    case 'member.role':
      if (c) await db.conversations.put({ ...c, members: c.members.map(m => m.account_id === d.account_id ? { ...m, role: String(d.role) } : m), me: d.account_id === myId ? { ...c.me, role: String(d.role) } : c.me });
      break;
    case 'me.member_prefs':
      if (c) {
        const me = { ...c.me, ...d }; delete me.conversation_id; me.cleared_seq = Math.max(Number(c.me.cleared_seq ?? 0), integer(d.cleared_seq));
        await putConversation(db, { ...c, me });
      }
      break;
    case 'pins.changed': await replacePins(db, Array.isArray(d.pins) ? d.pins as Pin[] : [], true); break;
    case 'contact.request.received': case 'contact.request.sent': case 'contact.request.resolved':
      if (d.account) await db.accounts.put(decodeAccount(d.account));
      if (d.request) { const r = d.request as FriendRequest; if (r.status === 'pending') await db.requests.put(r); else await db.requests.delete(r.id); }
      break;
    case 'contact.added': if (d.account) await db.accounts.put(decodeAccount(d.account)); if (d.contact) await db.contacts.put(d.contact as Contact); break;
    case 'contact.notes.changed':
      if (d.note && typeof d.note === 'object') { const note = d.note as { account_id: Id; revision?: number }; const old = await db.contactNotes.get(note.account_id); if (!old || Number(old.revision ?? 0) <= Number(note.revision ?? 0)) await db.contactNotes.put(note); }
      break;
    case 'contact.removed': await db.contacts.delete(String(d.account_id)); break;
    case 'block.changed':
      if (d.blocked) { const accountId = String(d.account_id); await db.blocks.put({ account_id: accountId }); await db.contacts.delete(accountId); const pending = await db.requests.filter(r => r.from_id === accountId || r.to_id === accountId).primaryKeys(); await db.requests.bulkDelete(pending); }
      else await db.blocks.delete(String(d.account_id)); break;
    case 'account.updated': if (d.account) { const a = decodeAccount(d.account); await db.accounts.put(a); const mine = await db.me.get('me'); if (mine?.value.account.id === a.id) await db.me.put({ id: 'me', value: { ...mine.value, account: a } }); } break;
    case 'me.updated': if (d.me) { const me = decodeMe(d.me); await db.me.put({ id: 'me', value: me }); await db.accounts.put(me.account); } break;
    case 'message.hidden':
      if (cid && Array.isArray(d.message_ids)) for (const id of d.message_ids) {
        const mid = String(id); const marker = await db.tombstones.get(mid); await db.tombstones.put({ id: mid, conversation_id: cid, deleted_at: marker?.deleted_at ?? null, hidden: true }); const m = await db.messages.get(mid); if (m) await db.messages.put({ ...redact(m), hidden: true });
        await reconcileReplies(db, cid, mid);
        if (c?.last_message?.id === mid) { await db.conversations.put({ ...c, last_message: null }); await db.fetches.put({ conversation_id: c.id }); }
      }
      break;
    case 'project.removed': {
      if (typeof d.erased_conversation_id === 'string') {
        const id = d.erased_conversation_id;
        await db.erasedConversations.put({ id });
        await db.conversations.delete(id); await db.conversationIntents.delete(id); await removePin(db, id); await db.drafts.delete(id); await db.readMarks.delete(id); await db.fetches.delete(id);
        await db.messages.where('[conversation_id+seq]').between([id, Dexie.minKey], [id, Dexie.maxKey]).delete();
        await db.outbox.where('conversation_id').equals(id).delete();
        await db.coverage.where('conversation_id').equals(id).delete(); await db.seen.where('conversation_id').equals(id).delete();
      }
      if (d.project_kept !== true && typeof d.project_id === 'string') {
        await db.projectAccess.delete(d.project_id);
        for (const c of await db.conversations.filter(c => c.project_id === d.project_id).toArray()) {
          if (c.me.access === 'project') await revokeConversationAccess(db, c.id, c.me.left_seq !== null);
          else await db.fetches.put({ conversation_id: c.id });
        }
      }
      break;
    }
    case 'project.updated':
      if (d.project && typeof d.project === 'object') { const project = d.project as { id: Id; kind: string }; await db.projectAccess.put({ id: project.id, kind: project.kind }); }
      break;
    case 'draft.changed': if (d.draft) await applyRemoteDraft(db, d.draft as Record<string, unknown>); break;
    case 'transcript.changed':
      if (d.transcript && typeof d.transcript === 'object') { const mediaId = String(d.media_id); await rememberTranscript(db, mediaId, String(d.message_id), d.transcript); await projectTranscript(db, mediaId); }
      break;
  }
}

export interface ApplyResult { pts: number; applied: Update[]; stop?: 'gap' | 'unknown' | 'fenced' }
export async function applyUpdates(db: AccountStore, updates: Update[], generation?: number): Promise<ApplyResult> {
  return db.transaction('rw', db.tables, async () => {
    const meta = await db.meta.get('sync');
    if (!meta || generation !== undefined && generation !== meta.generation) return { pts: meta?.pts ?? 0, applied: [], stop: 'fenced' };
    const myId = (await db.me.get('me'))?.value.account.id ?? db.accountId;
    const result: ApplyResult = { pts: meta.pts, applied: [] };
    for (const u of updates) {
      if (u.pts <= result.pts) continue;
      if (u.pts !== result.pts + 1) { result.stop = 'gap'; break; }
      if (!knownUpdateTypes.has(u.type)) { result.stop = 'unknown'; break; }
      await applyCore(db, u, myId); result.pts = u.pts; result.applied.push(u);
    }
    if (result.applied.length) await db.meta.put({ ...(await db.meta.get('sync') ?? meta), pts: result.pts });
    return result;
  });
}


/** Merge only intervals established by a server history page or ordered stream; a summary is not coverage. */
export async function recordCoverage(db: AccountStore, conversationId: Id, from: number, to: number, generation: number): Promise<void> {
  if (!Number.isSafeInteger(from) || !Number.isSafeInteger(to) || to < from || to < 1) return;
  from = Math.max(1, from);
  await db.transaction('rw', db.coverage, async () => {
    const held = await db.coverage.where('conversation_id').equals(conversationId).toArray();
    const joined = held.filter(row => row.generation === generation && row.to + 1 >= from && row.from - 1 <= to);
    for (const row of joined) { from = Math.min(from, row.from); to = Math.max(to, row.to); }
    await db.coverage.bulkDelete(joined.map(row => row.id));
    await db.coverage.put({ id: `${conversationId}:${from}`, conversation_id: conversationId, from, to, generation });
  });
}

/** Caller owns an all-store transaction; both explicit intent and accepted evidence share this queue. */
export async function queueReadMark(db: AccountStore, id: Id, seq: number): Promise<number> {
  const c = await db.conversations.get(id); if (!c || c.me.left_seq !== null || c.me.role === null) return 0;
  const old = await db.readMarks.get(id); const desired = Math.min(seq, c.last_seq);
  if (desired <= c.me.read_seq && !old) return 0;
  const target = Math.max(old?.seq ?? 0, desired);
  if (target < 1) return 0;
  await db.readMarks.put({ conversation_id: id, seq: target, attempts: old?.attempts ?? 0, next_attempt_at: 0 });
  await advanceLocalRead(db, id, target); return target;
}

/** Durable visibility evidence is valid only for the projection generation in which it was observed. */
export async function acceptSeen(db: AccountStore, id: Id, seqs: readonly number[]): Promise<number> {
  return db.transaction('rw', db.tables, async () => {
    const [meta, c, coverage] = await Promise.all([db.meta.get('sync'), db.conversations.get(id), db.coverage.where('conversation_id').equals(id).sortBy('from')]);
    if (!meta || !c || c.me.left_seq !== null || c.me.role === null) return 0;
    const ranges = coverage.filter(row => row.generation === meta.generation);
    for (const seq of new Set(seqs)) {
      if (!Number.isSafeInteger(seq) || seq <= c.me.read_seq || seq > c.last_seq || !ranges.some(row => row.from <= seq && row.to >= seq)) continue;
      const message = await db.messages.where('[conversation_id+seq]').equals([id, seq]).first();
      if (message && !message.hidden) await db.seen.put({ conversation_id: id, seq, generation: meta.generation });
    }
    const evidence = new Set((await db.seen.where('conversation_id').equals(id).toArray()).filter(row => row.generation === meta.generation).map(row => row.seq));
    const max = Math.min(c.last_seq, [...evidence].reduce((max, seq) => Math.max(max, seq), c.me.read_seq));
    const rows = await db.messages.where('[conversation_id+seq]').between([id, c.me.read_seq], [id, max], false, true).toArray();
    const blocked = new Set(await db.blocks.toCollection().primaryKeys());
    let position = Math.max(c.me.read_seq, c.me.joined_seq, Number(c.me.cleared_seq ?? 0)) + 1;
    let candidate = 0;
    for (const range of ranges) {
      if (range.to < position) continue;
      if (range.from > position || position > max) break;
      const end = Math.min(max, range.to);
      for (const row of rows.filter(row => row.seq >= position && row.seq <= end)) {
        const counts = row.sender_id !== db.accountId && row.kind !== 'system' && !row.deleted_at && !row.hidden && !blocked.has(row.sender_id);
        if (counts && !evidence.has(row.seq)) return candidate ? queueReadMark(db, id, candidate) : 0;
        if (counts) candidate = row.seq;
      }
      position = end + 1;
    }
    return candidate ? queueReadMark(db, id, candidate) : 0;
  });
}
